import {
    IonAlert,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonModal,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSegment,
    IonSegmentButton,
    IonSpinner,
    IonTitle,
    IonToast,
    IonToolbar,
    RefresherEventDetail,
    useIonRouter
} from "@ionic/react";

import {
    addOutline,
    arrowBackOutline,
    americanFootballOutline,
    lockClosedOutline,
    medkitOutline,
    peopleOutline,
    refreshOutline,
    shirtOutline
} from "ionicons/icons";

import {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";

import {
    useLocation,
    useParams
} from "react-router-dom";

import type {
    FantasyMainTab
} from "../utils/fantasyNavigation";

import {
    getFantasyMainTab,
    getFantasyMainTabPath,
    getFantasyPlayersSubmenuPath
} from "../utils/fantasyNavigation";

import {
    getFantasyInjuryInfo
} from "../utils/fantasyInjury";

import {
    DEFAULT_SCHEDULE_TIMEZONE,
    formatLocalMatchSchedule,
    formatLocalWaiverSchedule
} from "../services/fantasySchedule";

import FantasyHeaderActions from "../components/FantasyHeaderActions";

import Cookies from "js-cookie";

import "./FantasyPlayers.css";
import "./FantasyNavigation.css";

import FantasyPlayerModal from "../components/FantasyPlayerModal";


type FantasyAcquisitionMode =
    | "free_agents"
    | "waivers"
    | "release_waivers";

interface FantasyAvailablePlayer {
    id: string;
    name: string;
    positionId: string;
    position: string;
    teamId: string;
    teamName: string;
    weekProjection: number;
    isLocked: boolean;
    lockReason?: string;
    injury?: string;

    matchupDate?: string;
    matchupHour?: string;
    matchupOpponentName?: string;
    matchupLocation?: "vs" | "@";

    acquisitionMode: FantasyAcquisitionMode;
    isReleaseWaiver: boolean;
    releaseWaiverProcessAt?: string;
}

interface FantasyWaiverClaim {
    id: string;
    teamId?: string;
    playerId: string;
    dropPlayerId: string;
    createdAt: string;

    type?: "waiver" | "friday_release_waiver";

    status:
        | "pending"
        | "won"
        | "lost"
        | "invalid"
        | "cancelled";

    resolvedAt?: string;
    message?: string;
}

interface FantasyResolvedWaiverClaim {
    id: string;
    playerId: string;
    playerName: string;
    dropPlayerId: string;
status:
    | "won"
    | "lost"
    | "invalid"
    | "cancelled";


    message?: string;
    createdAt: string;
    resolvedAt?: string;
}

interface FantasyWaiverState {
    mode: "waivers" | "free_agents";
    isProcessing: boolean;
    cycleKey: string;
    opensAt: string;
    processAt: string;
    priorityPosition: number;
    priorityTotal: number;
    pendingClaims: FantasyWaiverClaim[];
    resolvedClaims: FantasyResolvedWaiverClaim[];
}

interface FantasyRosterPlayer {
    id: string;
    name: string;
    positionId: string;
    position: string;
    teamId: string;
    teamName: string;
    slot: string;
    isLocked: boolean;
    lockReason?: string;
    injury?: string;
}

interface FantasyPlayersState {
    success: boolean;
    message?: string;
    scheduleTimezone?: string;

    league: {
        id: string;
        name: string;
    };

    draft: {
        completed: boolean;
        message?: string;
    };

    week: {
        realRound: number;
        fantasyRound: number;
        status: string;
    };
    waivers: FantasyWaiverState;

    roster: {
        count: number;
        maxPlayers: number;
        canAddWithoutReplacement?: boolean;
        players: FantasyRosterPlayer[];
    };

    availablePlayers: FantasyAvailablePlayer[];
}

const apiBaseUrl =
    "https://fantasy.ftblscore.com/create_league";

function getPositionLabel(
    player: Pick<
        FantasyAvailablePlayer,
        "position" | "positionId"
    >
): string {
    const position = String(player.position || "")
        .trim()
        .toUpperCase();

    if (position !== "") {
        return position;
    }

    const positions: Record<string, string> = {
        "1": "QB",
        "2": "RB",
        "3": "WR",
        "7": "K",
        "8": "DEF"
    };

    return positions[String(player.positionId || "")] || "—";
}

function formatProjection(value: number): string {
    return Number.isFinite(value)
        ? value.toFixed(1)
        : "—";
}

function getAvailablePlayerMatchup(
    player: FantasyAvailablePlayer,
    scheduleTimezone: string
): string {
    const opponentName = String(
        player.matchupOpponentName || ""
    ).trim();

    if (!opponentName) {
        return "BYE";
    }

    const schedule = formatLocalMatchSchedule(
        player.matchupDate || "",
        player.matchupHour || "",
        scheduleTimezone
    );

    const location =
        player.matchupLocation === "@"
            ? "@"
            : "vs";

    return [
        schedule,
        location,
        opponentName
    ]
        .filter(Boolean)
        .join(" ");
}

function playerUsesWaivers(
    player: FantasyAvailablePlayer
): boolean {
    return (
        player.acquisitionMode === "waivers" ||
        player.acquisitionMode ===
            "release_waivers"
    );
}

function isFridayReleaseWaiver(
    player: FantasyAvailablePlayer
): boolean {
    return (
        player.acquisitionMode ===
        "release_waivers"
    );
}

function getPlayerActionLabel(
    player: FantasyAvailablePlayer
): string {
    return playerUsesWaivers(player)
        ? "Reclamar"
        : "Agregar";
}

function getWeekStatusLabel(status?: string): string {
    const normalizedStatus = String(status || "")
        .trim()
        .toLowerCase();

    if (normalizedStatus === "live") {
        return "En vivo";
    }

    if (normalizedStatus === "scheduled") {
        return "Próximamente";
    }

    if (normalizedStatus === "final") {
        return "Finalizada";
    }

    return "Sin información";
}

function getWaiverStatusLabel(
    status: FantasyResolvedWaiverClaim["status"]
): string {
    if (status === "won") {
        return "Ganado";
    }

    if (status === "lost") {
        return "No ganado";
    }
if (status === "cancelled") {
    return "Cancelado";
}

    return "No válido";
}

function getWaiverStatusClass(
    status: FantasyResolvedWaiverClaim["status"]
): string {
    if (status === "won") {
        return "fantasy-players-waiver-history__status--won";
    }

    if (status === "lost") {
        return "fantasy-players-waiver-history__status--lost";
    }
if (status === "cancelled") {
    return "fantasy-players-waiver-history__status--cancelled";
}

    return "fantasy-players-waiver-history__status--invalid";
}

function formatWaiverDate(value?: string): string {
    if (!value) {
        return "";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return "";
    }

    return new Intl.DateTimeFormat(
        "es-MX",
        {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit"
        }
    ).format(date);
}

function getAuthHeaders(): HeadersInit {
    return {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Access-Username":
            Cookies.get("access_username") || "",
        "X-Access-Token":
            Cookies.get("access_token") || ""
    };
}

const FantasyPlayers: React.FC = () => {
const router = useIonRouter();
const { id } = useParams<{ id: string }>();
const location = useLocation();

const activeMainTab = useMemo<
    FantasyMainTab | undefined
>(
    function (): FantasyMainTab | undefined {
        return getFantasyMainTab(
            location.pathname,
            id
        );
    },
    [id, location.pathname]
);

function navigateMainTab(
    selectedTab: FantasyMainTab
): void {
    if (!id || selectedTab === activeMainTab) {
        return;
    }

    router.push(
        getFantasyMainTabPath(
            selectedTab,
            id
        ),
        "none",
        "replace"
    );
}

    const [state, setState] =
        useState<FantasyPlayersState | null>(null);

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [
    claimToCancel,
    setClaimToCancel
] = useState<FantasyWaiverClaim | null>(null);

const [
    cancellingClaimId,
    setCancellingClaimId
] = useState<string | null>(null);

    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [positionFilter, setPositionFilter] =
    useState("all");


    const [
        selectedAvailablePlayer,
        setSelectedAvailablePlayer
    ] = useState<FantasyAvailablePlayer | null>(null);

    const [
    selectedFantasyPlayerId,
    setSelectedFantasyPlayerId
] = useState<string | null>(null);

const requestedPlayerId = useMemo(
    function (): string {
        const params = new URLSearchParams(
            location.search
        );

        return params.get("playerId") || "";
    },
    [location.search]
);


    function redirectToLogin() {
        router.push("/home", "root", "replace");
    }

    function isAuthenticated(): boolean {
        return Boolean(
            Cookies.get("access_token") &&
            Cookies.get("access_username")
        );
    }

    const loadPlayers = useCallback(
        async function (forceRefresh = false) {
            if (!isAuthenticated()) {
                redirectToLogin();
                return;
            }

            try {
                setError("");

                if (!forceRefresh) {
                    setLoading(true);
                } else {
                    setRefreshing(true);
                }

                const response = await fetch(
                    `${apiBaseUrl}/control_FantasyPlayersState.php` +
                    `?id=${encodeURIComponent(id)}` +
                    `&view=players`,
                    {
                        method: "GET",
                        credentials: "include",
                        headers: {
                            Accept: "application/json",
                            "X-Access-Username":
                                Cookies.get(
                                    "access_username"
                                ) || "",
                            "X-Access-Token":
                                Cookies.get(
                                    "access_token"
                                ) || ""
                        },
                        cache: "no-store"
                    }
                );

                const rawResponse = await response.text();

                let result: FantasyPlayersState;

                try {
                    result = JSON.parse(rawResponse);
                } catch {
                    throw new Error(
                        rawResponse.trim()
                            ? `El servidor devolvió una respuesta no válida. HTTP ${response.status}.`
                            : `El servidor devolvió una respuesta vacía. HTTP ${response.status}.`
                    );
                }

                if (!response.ok || !result.success) {
    throw new Error(
        result.message ||
            "No fue posible cargar los jugadores disponibles."
    );
}



                setState(result);
            } catch (requestError) {
                console.error(
                    "No fue posible cargar FantasyPlayers:",
                    requestError
                );

                setError(
                    requestError instanceof Error
                        ? requestError.message
                        : "No fue posible cargar los jugadores."
                );
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [id, router]
    );

    useEffect(
        function () {
            loadPlayers();
        },
        [loadPlayers]
    );

    useEffect(
    function () {
        if (!state ||
            !requestedPlayerId) {
            return;
        }

        const playerExists =
            state.availablePlayers.some(
                function (player) {
                    return player.id === requestedPlayerId;
                }
            );

        if (!playerExists) {
            setMessage(
                "El jugador ya no está disponible."
            );

            router.push(
    `/fantasyPlayers/${id}`,
    "none",
    "replace"
);
            return;
        }

        setSelectedFantasyPlayerId(
            requestedPlayerId
        );

        /*
            Quitamos playerId de la URL para que el modal
            no vuelva a abrirse después de un refresh.
        */
        router.push(
            `/fantasyPlayers/${id}`,
            "none",
            "replace"
        );
    },
    [
        state,
        requestedPlayerId,
        router,
        id
    ]
);

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadPlayers(true);
        event.detail.complete();
    }

function openAddPlayer(
    player: FantasyAvailablePlayer
) {
    if (!state) {
        setMessage(
            "No fue posible cargar el estado de la liga."
        );

        return;
    }

    if (!state.draft.completed) {
        setMessage(
            state.draft.message ||
                "No puedes agregar jugadores hasta que finalice el draft."
        );

        return;
    }

    if (isProcessingWaivers) {
        setMessage(
            "Los waivers se están procesando. Intenta nuevamente en unos momentos."
        );

        return;
    }

    if (player.isLocked) {
        setMessage(
            player.lockReason ||
                "No puedes agregar este jugador porque su partido ya inició."
        );

        return;
    }

    const alreadyClaimed =
        pendingWaiverClaims.some(
            function (claim) {
                return claim.playerId === player.id;
            }
        );

    if (alreadyClaimed) {
        setMessage(
            "Ya tienes un reclamo pendiente para este jugador."
        );

        return;
    }

    setSelectedAvailablePlayer(player);
}
    async function addPlayer(
    playerToReleaseId = ""
) {

        if (
            !state ||
            !selectedAvailablePlayer ||
            saving
        ) {
            return;
        }

        try {
            setSaving(true);

            const response = await fetch(
                `${apiBaseUrl}/control_AddFantasyPlayer.php`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: getAuthHeaders(),
                    body: JSON.stringify({
                    leagueId: id,
                    playerId: selectedAvailablePlayer.id,
                    dropPlayerId: playerToReleaseId
                })

                }
            );

            const rawResponse = await response.text();

let result: {
    success?: boolean;
    mode?:
        | "waiver_claim"
        | "friday_release_waiver_claim"
        | "free_agent";
    message?: string;
};
            try {
                result = JSON.parse(rawResponse);
            } catch {
                throw new Error(
                    rawResponse.trim()
                        ? `El servidor devolvió una respuesta no válida. HTTP ${response.status}.`
                        : `El servidor devolvió una respuesta vacía. HTTP ${response.status}.`
                );
            }

            if (!response.ok || !result.success) {
                throw new Error(
                    result.message ||
                        "No fue posible agregar al jugador."
                );
            }

            setSelectedAvailablePlayer(null);

setMessage(
    result.message ||
        (
            result.mode === "waiver_claim"
                ? `${selectedAvailablePlayer.name} fue solicitado por waivers.`
                : result.mode ===
                    "friday_release_waiver_claim"
                    ? `${selectedAvailablePlayer.name} fue solicitado para los waivers de liberados del viernes.`
                    : `${selectedAvailablePlayer.name} fue agregado a tu equipo.`
        )
);

            await loadPlayers(true);
        } catch (requestError) {
            console.error(
                "No fue posible agregar jugador Fantasy:",
                requestError
            );

            setMessage(
                requestError instanceof Error
                    ? requestError.message
                    : "No fue posible agregar al jugador."
            );
        } finally {
            setSaving(false);
        }
    }

    async function cancelWaiverClaim(
    claim: FantasyWaiverClaim
) {
    if (
        cancellingClaimId ||
        isProcessingWaivers
    ) {
        return;
    }

    try {
        setCancellingClaimId(claim.id);

        const response = await fetch(
            `${apiBaseUrl}/control_CancelFantasyWaiverClaim.php`,
            {
                method: "POST",
                credentials: "include",
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    leagueId: id,
                    claimId: claim.id
                })
            }
        );

        const rawResponse = await response.text();

        let result: {
            success?: boolean;
            message?: string;
        };

        try {
            result = JSON.parse(rawResponse);
        } catch {
            throw new Error(
                rawResponse.trim()
                    ? `El servidor devolvió una respuesta no válida. HTTP ${response.status}.`
                    : `El servidor devolvió una respuesta vacía. HTTP ${response.status}.`
            );
        }

        if (!response.ok || !result.success) {
            throw new Error(
                result.message ||
                    "No fue posible cancelar el reclamo."
            );
        }

        setMessage(
            result.message ||
                "Reclamo cancelado correctamente."
        );

        await loadPlayers(true);
    } catch (requestError) {
        console.error(
            "No fue posible cancelar el reclamo:",
            requestError
        );

        setMessage(
            requestError instanceof Error
                ? requestError.message
                : "No fue posible cancelar el reclamo."
        );
    } finally {
        setCancellingClaimId(null);
        setClaimToCancel(null);
    }
}

const filteredAvailablePlayers = useMemo(
    function (): FantasyAvailablePlayer[] {
        const players =
            state?.availablePlayers || [];

        if (positionFilter === "all") {
            return players;
        }

        return players.filter(
            function (player) {
                return (
                    getPositionLabel(player) ===
                    positionFilter
                );
            }
        );
    },
    [
        state?.availablePlayers,
        positionFilter
    ]
);

    const rosterPlayers = state?.roster.players || [];

        const availableReplacementPlayers =
    rosterPlayers.filter(function (player) {
        return !player.isLocked;
    });

    const canAddWithoutReplacement = Boolean(
        state?.roster.canAddWithoutReplacement
    );

    const isWaiverPeriod =
    state?.waivers.mode === "waivers";

const isProcessingWaivers = Boolean(
    state?.waivers.isProcessing
);

const selectedPlayerUsesWaivers = Boolean(
    selectedAvailablePlayer &&
    playerUsesWaivers(selectedAvailablePlayer)
);

const selectedPlayerIsFridayReleaseWaiver = Boolean(
    selectedAvailablePlayer &&
    isFridayReleaseWaiver(selectedAvailablePlayer)
);

const selectedPlayerHasPendingClaim = Boolean(
    selectedAvailablePlayer &&
    state?.waivers.pendingClaims.some(
        function (claim) {
            return (
                claim.playerId ===
                selectedAvailablePlayer.id
            );
        }
    )
);

async function handleClaimFromPlayerModal(
    playerId: string
) {
    const player = state?.availablePlayers.find(
        function (availablePlayer) {
            return availablePlayer.id === playerId;
        }
    );

    if (!player) {
        setMessage(
            "El jugador ya no está disponible."
        );

        setSelectedFantasyPlayerId(null);

        await loadPlayers(true);

        return;
    }

    /*
        Cerramos la ficha antes de abrir el modal de
        reclamar/agregar para evitar modales apilados.
    */
    setSelectedFantasyPlayerId(null);

    openAddPlayer(player);
}

const availablePlayers = state?.availablePlayers || [];

const pendingWaiverClaims =
    state?.waivers.pendingClaims || [];

const resolvedWaiverClaims =
    state?.waivers.resolvedClaims || [];

    const normalWaiverProcessAt =
    formatLocalWaiverSchedule(
        state?.waivers.processAt
    );

const selectedReleaseWaiverProcessAt =
    formatLocalWaiverSchedule(
        selectedAvailablePlayer?.releaseWaiverProcessAt
    );

const activePlayersSubmenu = useMemo(
    function (): string {
        if (
            location.pathname.includes(
                "/fantasyDropPlayers/"
            )
        ) {
            return "drop";
        }

        if (
            location.pathname.includes(
                "/fantasyTopPlayers/"
            )
        ) {
            return "leaders";
        }

        return "add";
    },
    [location.pathname]
);

    return (
        <IonPage>
            <IonHeader translucent>
<IonToolbar className="fantasy-players-toolbar">
<IonButtons slot="start">
    <IonButton
        fill="clear"
        aria-label="Volver al inicio de la liga"
        onClick={function () {
            router.push(
                `/fantasyLeague/${id}`,
                "back",
                "replace"
            );
        }}
    >
        <IonIcon icon={arrowBackOutline} />
    </IonButton>
</IonButtons>

    <IonTitle>Jugadores</IonTitle>
    <FantasyHeaderActions
        leagueId={id || ""}
        screen="players"
        showSettings={false}
    />

</IonToolbar>

 <IonToolbar className="fantasy-navigation-toolbar">
    <IonSegment
        value={activeMainTab || ""}
        onIonChange={function (event) {
            const selectedTab = String(
                event.detail.value || ""
            ) as FantasyMainTab;

            navigateMainTab(selectedTab);
        }}
    >
        <IonSegmentButton value="home">
            Inicio
        </IonSegmentButton>

        <IonSegmentButton value="players">
            Jugadores
        </IonSegmentButton>

        <IonSegmentButton value="team">
            Mi equipo
        </IonSegmentButton>

        <IonSegmentButton value="game">
            Juego
        </IonSegmentButton>
    </IonSegment>
</IonToolbar>

<IonToolbar className="fantasy-subnavigation-toolbar">
    <IonSegment
        value={activePlayersSubmenu}
        onIonChange={function (event) {
            const selectedTab = String(
                event.detail.value || "add"
            ) as "add" | "drop" | "leaders";

            if (selectedTab === activePlayersSubmenu) {
                return;
            }

            router.push(
                getFantasyPlayersSubmenuPath(selectedTab, id),
                "none",
                "replace"
            );
        }}
    >
        <IonSegmentButton value="add">
            Agregar
        </IonSegmentButton>

        <IonSegmentButton value="drop">
            Soltar
        </IonSegmentButton>

        <IonSegmentButton value="leaders">
            Líderes
        </IonSegmentButton>
    </IonSegment>
</IonToolbar>
            </IonHeader>

            <IonContent
                fullscreen
                className="fantasy-players-content"
            >
                <IonRefresher
                    slot="fixed"
                    onIonRefresh={handleRefresh}
                >
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando jugadores..."
                    />
                </IonRefresher>

                {loading && (
                    <IonProgressBar
                        type="indeterminate"
                        className="fantasy-players-progress"
                    />
                )}

                {!loading && error && (
                    <main className="fantasy-players-main">
                        <div className="fantasy-players-empty">
                            <IonIcon icon={refreshOutline} />

                            <strong>
                                No pudimos cargar los jugadores
                            </strong>

                            <span>{error}</span>

                            <IonButton
                                size="small"
                                fill="outline"
                                onClick={function () {
                                    loadPlayers(true);
                                }}
                            >
                                Reintentar
                            </IonButton>
                        </div>
                    </main>
                )}

                {!loading && !error && state && (
                    <><section className="fantasy-players-hero">
    <div className="fantasy-players-hero__icon">
        <IonIcon icon={peopleOutline} />
    </div>

    <div>
        <span>{state.league.name}</span>

        <h1>Jugadores disponibles</h1>

        <p>
            Consulta y filtra los jugadores libres de la liga.
        </p>
    </div>
</section>

<main className="fantasy-players-main">
    <section className="fantasy-players-summary">
        <div>
            <span>ROSTER</span>

            <strong>
                {state.roster.count} /{" "}
                {state.roster.maxPlayers}
            </strong>
        </div>

        <div>
            <span>JORNADA</span>

            <strong>
                {state.week.realRound || "—"}
            </strong>
        </div>

        <div>
            <span>ESTADO</span>

            <strong>
                {getWeekStatusLabel(
                    state.week.status
                )}
            </strong>
        </div>
    </section>
{!state.draft.completed && (
    <section className="fantasy-players-notice">
        <IonIcon icon={lockClosedOutline} />

        <div>
            <strong>
                Movimientos bloqueados
            </strong>

            <span>
                {state.draft.message ||
                    "No puedes agregar jugadores hasta que finalice el draft."}
            </span>
        </div>
    </section>
)}

{isProcessingWaivers ? (
    <section className="fantasy-players-notice">
        <IonIcon icon={refreshOutline} />

        <div>
            <strong>
                Procesando waivers
            </strong>

            <span>
                Los reclamos pendientes se están resolviendo.
                La agencia libre estará disponible en unos momentos.
            </span>
        </div>
    </section>
) : isWaiverPeriod ? (
    <section className="fantasy-players-notice">
        <IonIcon icon={peopleOutline} />

        <div>
            <strong>
                Periodo de waivers activo
            </strong>

            <span>
                Tu prioridad es #
                {state.waivers.priorityPosition || "—"} de{" "}
                {state.waivers.priorityTotal || "—"}.

                {normalWaiverProcessAt
                    ? ` Los reclamos se resuelven el ${normalWaiverProcessAt}.`
                    : " La fecha de resolución está pendiente."}
            </span>
        </div>
    </section>
) : null}
{pendingWaiverClaims.length > 0 && (
    <section className="fantasy-players-waiver-history">
        <div className="fantasy-players-section__heading">
            <div>
                <span>
                    WAIVERS
                </span>

                <h2>
                    Reclamos pendientes
                </h2>
            </div>
        </div>

        <div className="fantasy-players-waiver-history__list">
            {pendingWaiverClaims.map(
                function (claim) {
                    const player =
                        availablePlayers.find(
                            function (availablePlayer) {
                                return (
                                    availablePlayer.id ===
                                    claim.playerId
                                );
                            }
                        );


                    const isCancelling =
                        cancellingClaimId === claim.id;

                    return (
                        <article
                            key={claim.id}
                            className="fantasy-players-waiver-history__item"
                        >
                            <div>
                                <strong>
                                    {player?.name ||
                                        "Jugador reclamado"}
                                </strong>

                                <small>
                                    {claim.dropPlayerId
                                        ? "Reclamo con reemplazo pendiente"
                                        : "Reclamo pendiente"}
                                </small>
                            </div>

                            <div className="fantasy-players-waiver-history__actions">
                                <span className="fantasy-players-waiver-history__status fantasy-players-waiver-history__status--pending">
                                    Pendiente
                                </span>

                                <IonButton
                                    size="small"
                                    fill="clear"
                                    color="danger"
                                    disabled={
                                        isCancelling ||
                                        isProcessingWaivers
                                    }
                                    onClick={function () {
                                        setClaimToCancel(claim);
                                    }}
                                >
                                    {isCancelling ? (
                                        <IonSpinner
                                            name="crescent"
                                        />
                                    ) : (
                                        "Cancelar"
                                    )}
                                </IonButton>
                            </div>
                        </article>
                    );
                }
            )}
        </div>
    </section>
)}
{resolvedWaiverClaims.length > 0 && (
    <section className="fantasy-players-waiver-history">
        <div className="fantasy-players-section__heading">
            <div>
                <span>
                    HISTORIAL
                </span>

                <h2>
                    Waivers resueltos
                </h2>
            </div>
        </div>

        <div className="fantasy-players-waiver-history__list">
            {resolvedWaiverClaims.map(
                function (claim) {
                    return (
                        <article
                            key={claim.id}
                            className="fantasy-players-waiver-history__item"
                        >
                            <div>
                                <strong>
                                    {claim.playerName ||
                                        "Jugador"}
                                </strong>

                                <small>
                                    {claim.message ||
                                        "Reclamo procesado."}

                                    {claim.resolvedAt
                                        ? ` · ${formatWaiverDate(
                                            claim.resolvedAt
                                        )}`
                                        : ""}
                                </small>
                            </div>

                            <span
                                className={[
                                    "fantasy-players-waiver-history__status",
                                    getWaiverStatusClass(
                                        claim.status
                                    )
                                ].join(" ")}
                            >
                                {getWaiverStatusLabel(
                                    claim.status
                                )}
                            </span>
                        </article>
                    );
                }
            )}
        </div>
    </section>
)}

                            <section className="fantasy-players-filter">
                                <IonSegment
                                    value={positionFilter}
                                    scrollable
                                    onIonChange={function (event) {
                                        setPositionFilter(
                                            String(event.detail.value || "all")
                                        );
                                    } }
                                >
                                    <IonSegmentButton value="all">
                                        Todos
                                    </IonSegmentButton>

                                    <IonSegmentButton value="QB">
                                        QB
                                    </IonSegmentButton>

                                    <IonSegmentButton value="RB">
                                        RB
                                    </IonSegmentButton>

                                    <IonSegmentButton value="WR">
                                        WR
                                    </IonSegmentButton>

                                    <IonSegmentButton value="K">
                                        K
                                    </IonSegmentButton>

                                    <IonSegmentButton value="DEF">
                                        DEF
                                    </IonSegmentButton>
                                </IonSegment>
                            </section>


                            <section className="fantasy-players-section">
                                <div className="fantasy-players-section__heading">
                                    <div>
                                        <span>
                                            AGENCIA LIBRE
                                        </span>

                                        <h2>
                                            {positionFilter === "all"
                                                ? "Disponibles"
                                                : `Disponibles · ${positionFilter}`}{" "}
                                            ({filteredAvailablePlayers.length})
                                        </h2>

                                    </div>

                                    {refreshing && (
                                        <IonSpinner
                                            name="crescent" />
                                    )}
                                </div>

                                {filteredAvailablePlayers.length === 0 ? (

                                    <div className="fantasy-players-empty">
                                        <IonIcon
                                            icon={americanFootballOutline} />
<strong>
    No hay jugadores disponibles
</strong>

<span>
    No hay agentes libres que coincidan con el filtro seleccionado.
</span>
                                    </div>
                                ) : (
                                    <div className="fantasy-players-list">
                                        {filteredAvailablePlayers.map(

                                            function (player) {
                                                const position = getPositionLabel(
                                                    player
                                                );
const hasPendingClaim =
    pendingWaiverClaims.some(
        function (claim) {
            return claim.playerId === player.id;
        }
    );
    const injury = getFantasyInjuryInfo(
    player.injury
);


                                                return (
                                                    <article
    key={player.id}
    className={[
        "fantasy-players-card",
        "fantasy-players-card--selectable",
        player.isLocked
            ? "fantasy-players-card--locked"
            : ""
    ].join(" ")}
    onClick={function () {
        setSelectedFantasyPlayerId(player.id);
    }}
>

                                                        <span
                                                            className={`fantasy-players-position fantasy-players-position--${position.toLowerCase()}`}
                                                        >
                                                            {position}
                                                        </span>

                                                        <div className="fantasy-players-card__details">
                                                            <strong>
                                                                {player.name}
                                                            </strong>

                                                            <small className="fantasy-players-card__team">
                                                                {player.teamName || "Sin equipo"}
                                                            </small>

                                                            <small className="fantasy-players-card__matchup">
                                                                {getAvailablePlayerMatchup(
                                                                    player,
                                                                    state.scheduleTimezone ||
                                                                        DEFAULT_SCHEDULE_TIMEZONE
                                                                )}
                                                            </small>

                                                            {injury.status && (
                                                                <span
                                                                    className={[
                                                                        "fantasy-players-injury",
                                                                        `fantasy-players-injury--${injury.status}`
                                                                    ].join(" ")}
                                                                    title={injury.description}
                                                                >
                                                                    <IonIcon icon={medkitOutline} />

                                                                    {injury.label}
                                                                </span>
                                                            )}

                                                            {player.isLocked && (
                                                                <em>
                                                                    <IonIcon
                                                                        icon={lockClosedOutline} />

                                                                    {player.lockReason ||
                                                                        "Partido iniciado"}
                                                                </em>
                                                            )}
                                                        </div>

                                                        <div className="fantasy-players-card__projection">
                                                            <span>
                                                                PROY.
                                                            </span>

                                                            <strong>
                                                                {formatProjection(
                                                                    player.weekProjection
                                                                )}
                                                            </strong>

                                                            <small>
                                                                PTS
                                                            </small>
                                                        </div>

<IonButton
    size="small"
    disabled={
        !state?.draft.completed ||
        player.isLocked ||
        saving ||
        isProcessingWaivers ||
        hasPendingClaim
    }
    onClick={function (event) {
        event.stopPropagation();
        openAddPlayer(player);
    }}
>
    <IonIcon
        slot="start"
        icon={addOutline}
    />

{hasPendingClaim
    ? "Reclamado"
    : getPlayerActionLabel(player)}
</IonButton>


                                                    </article>
                                                );
                                            }
                                        )}
                                    </div>
                                )}
                            </section>
                        </main></>
                )}
            </IonContent>
<FantasyPlayerModal
    leagueId={id || ""}
    playerId={selectedFantasyPlayerId || ""}
    isOpen={selectedFantasyPlayerId !== null}
    onDismiss={function () {
        setSelectedFantasyPlayerId(null);
    }}
    onClaimPlayer={handleClaimFromPlayerModal}
/>


            <IonModal
                isOpen={Boolean(selectedAvailablePlayer)}
                onDidDismiss={function () {
                    if (!saving) {
                        setSelectedAvailablePlayer(null);
                    }
                }}
            >
                <IonHeader>
                    <IonToolbar>
                        <IonButton
                            slot="start"
                            fill="clear"
                            disabled={
    saving ||
    selectedPlayerHasPendingClaim ||
    isProcessingWaivers
}

                            onClick={function () {
                                setSelectedAvailablePlayer(null);
                            }}
                        >
                            <IonIcon icon={arrowBackOutline} />
                        </IonButton>
<IonTitle>
    {selectedPlayerUsesWaivers
        ? "Reclamar jugador"
        : "Agregar jugador"}
</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="fantasy-players-modal">
                    {selectedAvailablePlayer && (
                        <main className="fantasy-players-modal__main">
                            <section className="fantasy-players-modal__hero">
                                <span>
                                    {getPositionLabel(
                                        selectedAvailablePlayer
                                    )}
                                </span>

                                <h2>
                                    {
                                        selectedAvailablePlayer.name
                                    }
                                </h2>

                                <p>
                                    {
                                        selectedAvailablePlayer.teamName
                                    }{" "}
                                    · Proyección:{" "}
                                    {formatProjection(
                                        selectedAvailablePlayer.weekProjection
                                    )}{" "}
                                    pts
                                </p>
                            </section>
{selectedPlayerIsFridayReleaseWaiver ? (
    <section className="fantasy-players-waiver-notice">
        <strong>
            Reclamo de waivers
        </strong>

        <span>
            Tu prioridad actual es #
            {state?.waivers.priorityPosition || "—"} de{" "}
            {state?.waivers.priorityTotal || "—"}.

            {selectedReleaseWaiverProcessAt
                ? ` El reclamo se resolverá el ${selectedReleaseWaiverProcessAt}.`
                : " La fecha de resolución está pendiente."}
        </span>

    </section>
) : selectedPlayerUsesWaivers ? (
    <section className="fantasy-players-waiver-notice">
        <strong>
            Reclamo de waivers
        </strong>

        <span>
            Tu prioridad actual es #
            {state?.waivers.priorityPosition || "—"} de{" "}
            {state?.waivers.priorityTotal || "—"}.

            {normalWaiverProcessAt
                ? ` El reclamo se resolverá el ${normalWaiverProcessAt}.`
                : " La fecha de resolución está pendiente."}
        </span>
    </section>
) : null}

                            {canAddWithoutReplacement ? (
                                <section className="fantasy-players-modal__action">
                                    <p>
                                        Tu roster tiene un espacio
                                        disponible.
                                    </p>

                                    <IonButton
                                        expand="block"
                                        disabled={
    saving ||
    selectedPlayerHasPendingClaim ||
    isProcessingWaivers
}

                                        onClick={function () {
                                            addPlayer("");
                                        }}
                                    >
{saving
    ? (
        selectedPlayerUsesWaivers
            ? "Registrando reclamo..."
            : "Agregando..."
    )
    : (
        selectedPlayerUsesWaivers
            ? "Crear reclamo"
            : "Agregar a mi equipo"
    )}

                                    </IonButton>
                                </section>
                            ) : (
                                <section className="fantasy-players-modal__replacement">
                                    <span>
                                        ROSTER COMPLETO
                                    </span>

                                    <h3>
                                        Selecciona un jugador para
                                        reemplazar
                                    </h3>

                                    <p>
                                        No podrás soltar jugadores
                                        cuyo partido ya haya iniciado.
                                    </p>

                                    {availableReplacementPlayers.length === 0 ? (
    <div className="fantasy-players-empty">
        <IonIcon icon={lockClosedOutline} />

        <strong>
    No hay jugadores para este filtro
</strong>

<span>
    No hay jugadores disponibles en la posición
    seleccionada.
</span>

    </div>
) : (
    availableReplacementPlayers.map(
        function (rosterPlayer) {
            return (
                <button
                    key={rosterPlayer.id}
                    type="button"
                    className="fantasy-players-replacement"
                    disabled={saving}

                    onClick={function () {
                        addPlayer(rosterPlayer.id);
                    }}
                >
                    <span className="fantasy-players-replacement__position">
                        {rosterPlayer.position}
                    </span>

                    <span className="fantasy-players-replacement__details">
                        <strong>
                            {rosterPlayer.name}
                        </strong>

                        <small>
                            {rosterPlayer.teamName} ·{" "}
                            {rosterPlayer.slot || "BN"}
                        </small>
                    </span>

                    <IonIcon icon={shirtOutline} />
                </button>
            );
        }
    )
)}

                                </section>
                            )}

                            <IonButton
                                expand="block"
                                fill="clear"
                                color="medium"
                                disabled={saving}

                                onClick={function () {
                                    setSelectedAvailablePlayer(null);
                                }}
                            >
                                Cancelar
                            </IonButton>
                        </main>
                    )}
                </IonContent>
            </IonModal>
<IonAlert
    isOpen={claimToCancel !== null}
    header="¿Cancelar reclamo?"
    message="El jugador volverá a estar disponible y podrás crear otro reclamo mientras el periodo de waivers siga abierto."
    onDidDismiss={function () {
        if (!cancellingClaimId) {
            setClaimToCancel(null);
        }
    }}
    buttons={[
        {
            text: "Conservar reclamo",
            role: "cancel"
        },
        {
            text: "Cancelar reclamo",
            role: "destructive",
            handler: function () {
                if (claimToCancel) {
                    void cancelWaiverClaim(
                        claimToCancel
                    );
                }
            }
        }
    ]}
/>

            <IonToast
                isOpen={Boolean(message)}
                message={message}
                duration={3400}
                position="bottom"
                onDidDismiss={function () {
                    setMessage("");
                }}
            />
        </IonPage>
    );
};

export default FantasyPlayers;
